import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Check, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrivateBrief = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    purchase_purpose: '',
    purchase_purpose_other: '',
    preferred_areas: [],
    preferred_areas_other: '',
    property_type: '',
    min_bedrooms: '',
    surroundings: [],
    essential_features: '',
    desirable_features: '',
    property_description: '',
    budget_min: '',
    budget_max: '',
    purchase_timeframe: '',
    payment_method: '',
    budget_flexible: null,
    visited_area: null,
    full_name: '',
    email: '',
    phone: '',
    country_of_residence: '',
    preferred_contact_method: '',
    best_time_to_contact: '',
    additional_information: '',
    data_consent: false
  });

  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!formData.data_consent) {
      toast.error(t('brief.errors.consent'));
      return;
    }
    
    setLoading(true);
    try {
      const cleanedData = {
        ...formData,
        min_bedrooms: formData.min_bedrooms === '' ? null : parseInt(formData.min_bedrooms),
        budget_min: formData.budget_min === '' ? null : parseFloat(formData.budget_min),
        budget_max: formData.budget_max === '' ? null : parseFloat(formData.budget_max)
      };
      
      await axios.post(`${API}/briefs`, cleanedData);
      setSubmitted(true);
    } catch {
      toast.error(t('brief.errors.submit'));
    } finally {
      setLoading(false);
    }
  };

  // Success State
  if (submitted) {
    return (
      <div className="min-h-screen bg-background pt-32 pb-20" data-testid="brief-success">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8">
              <Check size={40} className="text-primary" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-6">
              {t('brief.success.title')}
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed mb-10">
              {t('brief.success.message')}
            </p>
            <Button onClick={() => navigate('/')} variant="outline" className="btn-outline">
              {t('brief.success.backHome')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const purposeKeys = ['permanent_residence', 'holiday_home', 'relocation', 'investment', 'exploring', 'other'];
  const areaKeys = ['lisbon', 'cascais', 'sintra', 'porto', 'algarve', 'silver_coast', 'alentejo', 'central', 'madeira', 'open', 'other'];
  const propertyTypeKeys = ['villa', 'apartment', 'townhouse', 'country_estate', 'land', 'no_preference'];
  const surroundingKeys = ['city', 'coast', 'countryside', 'golf', 'mixed'];
  const timeframeKeys = ['immediately', '3_months', '6_months', '12_months', 'flexible'];
  const contactMethodKeys = ['email', 'phone', 'whatsapp'];

  return (
    <div className="min-h-screen bg-background" data-testid="private-brief">
      {/* Header with Progress */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b shadow-sm">
        <div className="container-custom py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Portugal Elite Estates" className="w-8 h-8" />
            <span className="font-heading font-semibold text-foreground hidden sm:block">{t('brief.header')}</span>
          </div>
          <span className="text-sm text-muted-foreground">
            {t('brief.step', { current: currentStep, total: totalSteps })}
          </span>
        </div>
        <Progress value={progress} className="h-1 rounded-none" />
      </div>

      <div className="pt-28 pb-20">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            {/* Back Button */}
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft size={18} />
                {t('brief.buttons.back')}
              </button>
            )}

            {/* Step 1 - Your Plans */}
            {currentStep === 1 && (
              <div className="space-y-8" data-testid="step-1">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-medium text-foreground mb-3">
                    {t('brief.step1.title')}
                  </h2>
                  <p className="text-muted-foreground">{t('brief.step1.subtitle')}</p>
                </div>

                <div className="space-y-3">
                  {purposeKeys.map((key) => (
                    <button
                      key={key}
                      onClick={() => updateField('purchase_purpose', key)}
                      className={`option-card w-full p-5 text-left rounded-xl bg-white border-2 flex items-center justify-between ${
                        formData.purchase_purpose === key ? 'selected border-primary' : 'border-gray-100'
                      }`}
                      data-testid={`purpose-${key}`}
                    >
                      <span className="font-medium text-foreground">{t(`brief.purposes.${key}`)}</span>
                      {formData.purchase_purpose === key && (
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <Check size={14} className="text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {formData.purchase_purpose === 'other' && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('brief.otherPlaceholder')}</label>
                    <Input
                      value={formData.purchase_purpose_other}
                      onChange={(e) => updateField('purchase_purpose_other', e.target.value)}
                      className="h-12"
                    />
                  </div>
                )}

                <Button 
                  onClick={handleNext}
                  disabled={!formData.purchase_purpose}
                  className="btn-primary w-full sm:w-auto"
                  data-testid="next-btn"
                >
                  {t('brief.buttons.next')}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            )}

            {/* Step 2 - Preferred Areas */}
            {currentStep === 2 && (
              <div className="space-y-8" data-testid="step-2">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-medium text-foreground mb-3">
                    {t('brief.step2.title')}
                  </h2>
                  <p className="text-muted-foreground">{t('brief.step2.subtitle')}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {areaKeys.map((key) => (
                    <button
                      key={key}
                      onClick={() => toggleArrayField('preferred_areas', key)}
                      className={`option-card p-4 text-left rounded-xl bg-white border-2 flex items-center justify-between ${
                        formData.preferred_areas.includes(key) ? 'selected border-primary' : 'border-gray-100'
                      }`}
                      data-testid={`area-${key}`}
                    >
                      <span className="font-medium text-foreground text-sm">{t(`brief.areas.${key}`)}</span>
                      {formData.preferred_areas.includes(key) && (
                        <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                          <Check size={12} className="text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>

                {formData.preferred_areas.includes('other') && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('brief.otherAreaPlaceholder')}</label>
                    <Input
                      value={formData.preferred_areas_other}
                      onChange={(e) => updateField('preferred_areas_other', e.target.value)}
                      className="h-12"
                    />
                  </div>
                )}

                <Button 
                  onClick={handleNext}
                  disabled={formData.preferred_areas.length === 0}
                  className="btn-primary w-full sm:w-auto"
                  data-testid="next-btn"
                >
                  {t('brief.buttons.next')}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            )}

            {/* Step 3 - The Property */}
            {currentStep === 3 && (
              <div className="space-y-8" data-testid="step-3">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-medium text-foreground mb-3">
                    {t('brief.step3.title')}
                  </h2>
                  <p className="text-muted-foreground">{t('brief.step3.subtitle')}</p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-3 block">{t('brief.step3.propertyType')}</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {propertyTypeKeys.map((key) => (
                      <button
                        key={key}
                        onClick={() => updateField('property_type', key)}
                        className={`option-card p-4 text-left rounded-xl bg-white border-2 flex items-center justify-between ${
                          formData.property_type === key ? 'selected border-primary' : 'border-gray-100'
                        }`}
                        data-testid={`type-${key}`}
                      >
                        <span className="font-medium text-foreground text-sm">{t(`brief.propertyTypes.${key}`)}</span>
                        {formData.property_type === key && (
                          <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <Check size={12} className="text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t('brief.step3.minBedrooms')}</label>
                  <Select value={formData.min_bedrooms} onValueChange={(v) => updateField('min_bedrooms', v)}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder={t('brief.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1+</SelectItem>
                      <SelectItem value="2">2+</SelectItem>
                      <SelectItem value="3">3+</SelectItem>
                      <SelectItem value="4">4+</SelectItem>
                      <SelectItem value="5">5+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-3 block">{t('brief.step3.surroundings')}</label>
                  <div className="flex flex-wrap gap-2">
                    {surroundingKeys.map((key) => (
                      <button
                        key={key}
                        onClick={() => toggleArrayField('surroundings', key)}
                        className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all ${
                          formData.surroundings.includes(key)
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-foreground border-gray-200 hover:border-primary/50'
                        }`}
                      >
                        {t(`brief.surroundings.${key}`)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t('brief.step3.essential')}</label>
                  <Textarea
                    value={formData.essential_features}
                    onChange={(e) => updateField('essential_features', e.target.value)}
                    placeholder={t('brief.step3.essentialPlaceholder')}
                    rows={3}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t('brief.step3.desirable')}</label>
                  <Textarea
                    value={formData.desirable_features}
                    onChange={(e) => updateField('desirable_features', e.target.value)}
                    placeholder={t('brief.step3.desirablePlaceholder')}
                    rows={3}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t('brief.step3.describe')}</label>
                  <Textarea
                    value={formData.property_description}
                    onChange={(e) => updateField('property_description', e.target.value)}
                    placeholder={t('brief.step3.describePlaceholder')}
                    rows={4}
                  />
                </div>

                <Button 
                  onClick={handleNext}
                  disabled={!formData.property_type}
                  className="btn-primary w-full sm:w-auto"
                  data-testid="next-btn"
                >
                  {t('brief.buttons.next')}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            )}

            {/* Step 4 - Budget and Timing */}
            {currentStep === 4 && (
              <div className="space-y-8" data-testid="step-4">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-medium text-foreground mb-3">
                    {t('brief.step4.title')}
                  </h2>
                  <p className="text-muted-foreground">{t('brief.step4.subtitle')}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('brief.step4.budgetMin')}</label>
                    <Input
                      type="number"
                      value={formData.budget_min}
                      onChange={(e) => updateField('budget_min', e.target.value)}
                      placeholder="300000"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('brief.step4.budgetMax')}</label>
                    <Input
                      type="number"
                      value={formData.budget_max}
                      onChange={(e) => updateField('budget_max', e.target.value)}
                      placeholder="750000"
                      className="h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t('brief.step4.timeframe')}</label>
                  <Select value={formData.purchase_timeframe} onValueChange={(v) => updateField('purchase_timeframe', v)}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder={t('brief.select')} />
                    </SelectTrigger>
                    <SelectContent>
                      {timeframeKeys.map(key => (
                        <SelectItem key={key} value={key}>{t(`brief.timeframes.${key}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t('brief.step4.payment')}</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => updateField('payment_method', 'cash')}
                      className={`flex-1 p-4 rounded-xl border-2 text-center ${
                        formData.payment_method === 'cash' ? 'border-primary bg-primary/5' : 'border-gray-100'
                      }`}
                    >
                      <span className="font-medium">{t('brief.step4.cash')}</span>
                    </button>
                    <button
                      onClick={() => updateField('payment_method', 'financing')}
                      className={`flex-1 p-4 rounded-xl border-2 text-center ${
                        formData.payment_method === 'financing' ? 'border-primary bg-primary/5' : 'border-gray-100'
                      }`}
                    >
                      <span className="font-medium">{t('brief.step4.financing')}</span>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-3 block">{t('brief.step4.flexible')}</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => updateField('budget_flexible', true)}
                      className={`px-6 py-3 rounded-full border-2 ${
                        formData.budget_flexible === true ? 'border-primary bg-primary text-white' : 'border-gray-200'
                      }`}
                    >
                      {t('brief.step4.yes')}
                    </button>
                    <button
                      onClick={() => updateField('budget_flexible', false)}
                      className={`px-6 py-3 rounded-full border-2 ${
                        formData.budget_flexible === false ? 'border-primary bg-primary text-white' : 'border-gray-200'
                      }`}
                    >
                      {t('brief.step4.no')}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-3 block">{t('brief.step4.visited')}</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => updateField('visited_area', true)}
                      className={`px-6 py-3 rounded-full border-2 ${
                        formData.visited_area === true ? 'border-primary bg-primary text-white' : 'border-gray-200'
                      }`}
                    >
                      {t('brief.step4.yes')}
                    </button>
                    <button
                      onClick={() => updateField('visited_area', false)}
                      className={`px-6 py-3 rounded-full border-2 ${
                        formData.visited_area === false ? 'border-primary bg-primary text-white' : 'border-gray-200'
                      }`}
                    >
                      {t('brief.step4.notYet')}
                    </button>
                  </div>
                </div>

                <Button 
                  onClick={handleNext}
                  className="btn-primary w-full sm:w-auto"
                  data-testid="next-btn"
                >
                  {t('brief.buttons.next')}
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </div>
            )}

            {/* Step 5 - Contact Details */}
            {currentStep === 5 && (
              <div className="space-y-8" data-testid="step-5">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-medium text-foreground mb-3">
                    {t('brief.step5.title')}
                  </h2>
                  <p className="text-muted-foreground">{t('brief.step5.subtitle')}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium mb-2 block">{t('brief.step5.fullName')} *</label>
                    <Input
                      required
                      value={formData.full_name}
                      onChange={(e) => updateField('full_name', e.target.value)}
                      className="h-12"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('brief.step5.email')} *</label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className="h-12"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('brief.step5.phone')}</label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder="+351 912 345 678"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('brief.step5.country')}</label>
                    <Input
                      value={formData.country_of_residence}
                      onChange={(e) => updateField('country_of_residence', e.target.value)}
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('brief.step5.contactMethod')}</label>
                    <Select value={formData.preferred_contact_method} onValueChange={(v) => updateField('preferred_contact_method', v)}>
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder={t('brief.select')} />
                      </SelectTrigger>
                      <SelectContent>
                        {contactMethodKeys.map(key => (
                          <SelectItem key={key} value={key}>{t(`brief.contactMethods.${key}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t('brief.step5.bestTime')}</label>
                  <Input
                    value={formData.best_time_to_contact}
                    onChange={(e) => updateField('best_time_to_contact', e.target.value)}
                    placeholder={t('brief.step5.bestTimePlaceholder')}
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">{t('brief.step5.additional')}</label>
                  <Textarea
                    value={formData.additional_information}
                    onChange={(e) => updateField('additional_information', e.target.value)}
                    placeholder={t('brief.step5.additionalPlaceholder')}
                    rows={4}
                  />
                </div>

                <div className="flex items-start gap-3 p-4 bg-secondary/30 rounded-xl">
                  <Checkbox
                    id="consent"
                    checked={formData.data_consent}
                    onCheckedChange={(checked) => updateField('data_consent', checked)}
                  />
                  <label htmlFor="consent" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                    {t('brief.step5.consent')}
                  </label>
                </div>

                <Button 
                  onClick={handleSubmit}
                  disabled={loading || !formData.full_name || !formData.email || !formData.data_consent}
                  className="btn-primary w-full sm:w-auto"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={18} />
                      {t('brief.buttons.submitting')}
                    </>
                  ) : (
                    <>
                      {t('brief.buttons.submit')}
                      <ArrowRight className="ml-2" size={18} />
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivateBrief;
